/*
 * How often the full-screen loader appears, and why. Read by the staging build stamp
 * (clusterService's stamp extra), so a "the loading screen keeps flashing" report can be
 * answered with a count and a trigger instead of a guess.
 *
 * Only the Footer's metrics refetch is counted: it is the one loader that fires from
 * background events, so it is the one that can flash when nobody asked for anything.
 */
interface LoaderStats {
  shown: number;
  quiet: number;
  lastTrigger: string;
  lastMs: number;
  longestMs: number;
  byTrigger: Record<string, number>;
}

const stats: LoaderStats = { shown: 0, quiet: 0, lastTrigger: "", lastMs: 0, longestMs: 0, byTrigger: {} };
let shownAt = 0;

/** The loader went up, for this reason. */
export const loaderShown = (trigger: string) => {
  stats.shown++;
  stats.lastTrigger = trigger;
  stats.byTrigger[trigger] = (stats.byTrigger[trigger] || 0) + 1;
  shownAt = performance.now();
};

/** The loader came down. */
export const loaderHidden = () => {
  if (!shownAt) return;
  stats.lastMs = Math.round(performance.now() - shownAt);
  stats.longestMs = Math.max(stats.longestMs, stats.lastMs);
  shownAt = 0;
};

/** A background refresh that would have raised the loader and, by design, did not. */
export const refreshedQuietly = () => {
  stats.quiet++;
};

export const loaderStatsText = (): string => {
  const why = Object.keys(stats.byTrigger).map((k) => `${k} ${stats.byTrigger[k]}`).join(", ");
  return (
    `loader: shown ${stats.shown}` +
    (stats.shown ? ` (${why}; last ${stats.lastMs}ms, longest ${stats.longestMs}ms)` : "") +
    `, background refreshes without it ${stats.quiet}`
  );
};
